// @ts-check

/** @file Holds one app-model subscription handover for deterministic browser regressions. */

import FrontendModelQuery from "velocious/build/src/frontend-models/query.js"
import {TaskBoard} from "../frontend-models/task-board"

/** Test-only subscription gate exposed through the existing Scoundrel registration. */
export default class BoardSubscriptionProbe {
  /** @type {typeof TaskBoard.onUpdate | null} */
  static originalOnUpdate = null
  static paused = false
  /** @type {(() => void) | null} */
  static releaseGate = null

  /**
   * Delays registration of the ID filter containing this newly loaded placement.
   * @param {string} taskBoardId Placement whose handover is held.
   * @returns {void} Nothing.
   * @throws {Error} When another gate is already armed.
   */
  static arm(taskBoardId) {
    if (this.originalOnUpdate) throw new Error("A board subscription gate is already armed")
    const released = Promise.withResolvers()
    const original = TaskBoard.onUpdate
    this.originalOnUpdate = original
    this.paused = false
    this.releaseGate = () => released.resolve(undefined)
    TaskBoard.onUpdate = async (callback, options) => {
      const where = options instanceof FrontendModelQuery ? options.eventFilterPayload()?.where : undefined
      if (Array.isArray(where?.id) && where.id.includes(taskBoardId)) {
        this.paused = true
        await released.promise
      }
      return await original.call(TaskBoard, callback, options)
    }
  }

  /**
   * Checks that the registration reached the gate.
   * @throws {Error} Until the matching registration is held.
   */
  static assertPaused() {
    if (!this.paused) throw new Error("Waiting for the loaded-ID subscription handover")
  }

  /** Restores the app-model method and releases any pending registration. */
  static release() {
    if (this.originalOnUpdate) TaskBoard.onUpdate = this.originalOnUpdate
    if (this.releaseGate) this.releaseGate()
    this.originalOnUpdate = null
    this.paused = false
    this.releaseGate = null
  }
}
